/**
 * MainLayout.tsx
 * Primary authenticated layout for all admin panel pages.
 *
 * Renders a white, collapsible sidebar whose navigation is split into three
 * role-filtered groups (Operations / Resources / Administration), a top header
 * bar with a dynamic slot for page-specific controls, and the routed page
 * content via <Outlet />.
 *
 * Sidebar behaviour:
 * - Active item: navy-tinted surface, navy label, and a brand-yellow dot.
 * - Collapses to an icon-only rail via the circular edge toggle (the sole
 *   collapse/expand control — there is no duplicate header trigger).
 * - Drag-to-resize via a thin handle on the right edge.
 *
 * The top header bar is hidden on the Scheduler route so the Bryntum timeline
 * (which has its own toolbar) reclaims the full viewport height.
 */

import React, { Suspense, useCallback } from 'react';
import kabbiLogo from '@/assets/images/kabbi-logo-gelb.png';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  LayoutDashboard,
  Radio,
  ClipboardList,
  Car,
  Repeat2,
  CalendarClock,
  CalendarCheck,
  Map,
  Truck,
  Building2,
  Users,
  BarChart3,
  FolderDown,
  FileStack,
  ScrollText,
  Tablet,
  Settings,
  Shield,
  LayoutGrid,
  Puzzle,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/store/authStore.ts';
import { usePreferencesStore } from '@/store/preferencesStore.ts';
import { useHeaderSlotStore } from '@/store/headerSlotStore.ts';

/** Minimum sidebar width in pixels when drag-resizing. */
const MIN_SIDEBAR_WIDTH = 200;

/** Default sidebar width in pixels on first load (matches the new design). */
const DEFAULT_SIDEBAR_WIDTH = 232;

/**
 * Icon tints, keyed by hue rather than by meaning so one entry can serve
 * several items.
 *
 * Lucide strokes every icon with `currentColor`, so a text colour on the svg
 * is the whole mechanism — no icon library change and no per-icon assets. The
 * class sits on the svg ITSELF, which is why it survives the menu button's
 * `hover:text-primary` and `data-[active=true]:text-primary`: those set the
 * colour of the button, and the svg's own colour wins over inheritance. The
 * icon therefore keeps its hue in every state, including the collapsed rail
 * where it is the only label a user has.
 *
 * Each shade is paired with a lighter `dark:` variant — a 500/600 sits too
 * close to black on the dark themes to be legible.
 */
const ICON_COLOR = {
  indigo:  'text-indigo-500 dark:text-indigo-400',
  sky:     'text-sky-500 dark:text-sky-400',
  blue:    'text-blue-600 dark:text-blue-400',
  cyan:    'text-cyan-600 dark:text-cyan-400',
  violet:  'text-violet-500 dark:text-violet-400',
  purple:  'text-purple-500 dark:text-purple-400',
  teal:    'text-teal-600 dark:text-teal-400',
  emerald: 'text-emerald-600 dark:text-emerald-400',
  green:   'text-green-600 dark:text-green-400',
  lime:    'text-lime-600 dark:text-lime-400',
  amber:   'text-amber-600 dark:text-amber-400',
  orange:  'text-orange-500 dark:text-orange-400',
  rose:    'text-rose-500 dark:text-rose-400',
  pink:    'text-pink-500 dark:text-pink-400',
  fuchsia: 'text-fuchsia-500 dark:text-fuchsia-400',
  /** System-level entries that should recede rather than compete. */
  muted:   'text-slate-400 dark:text-slate-500',
} as const;

/** A single navigation entry. */
interface NavItem {
  /** i18n key for the visible label. */
  titleKey: string;
  /** Lucide icon shown left of the label. */
  icon: LucideIcon;
  /** Route path. */
  path: string;
  /**
   * Icon tint from `ICON_COLOR`. Required, not optional: a new nav item with
   * no colour would render grey among coloured neighbours and read as
   * disabled, so the type forces the choice at the point the item is added.
   */
  color: string;
  /** If present, the item only shows for users holding one of these roles. */
  roles?: string[];
}

/** A labelled group of navigation entries. */
interface NavGroup {
  /** i18n key for the uppercase group heading. */
  labelKey: string;
  items: NavItem[];
}

const STAFF = ['ADMIN', 'DISPATCHER'];
const ADMIN = ['ADMIN'];

/**
 * Sidebar navigation, split into three groups. Item order and grouping follow
 * the new design; our extra surfaces with no design slot (Tour Planning, Rules,
 * Plugins) are placed in the closest-fitting group. Role gates are preserved
 * from the previous single-list nav.
 */
const NAV_GROUPS: NavGroup[] = [
  // Operations — cool blues, so the day-to-day block reads as one family.
  {
    labelKey: 'nav.groups.operations',
    items: [
      { titleKey: 'nav.dashboard',      icon: LayoutDashboard, path: '/dashboard',       color: ICON_COLOR.indigo },
      { titleKey: 'nav.dispatcher',     icon: Radio,           path: '/dispatcher',      color: ICON_COLOR.sky },
      { titleKey: 'nav.orders',         icon: ClipboardList,   path: '/orders',          color: ICON_COLOR.blue },
      { titleKey: 'nav.toursDetails',   icon: Car,             path: '/tours-details',   color: ICON_COLOR.cyan },
      { titleKey: 'nav.permanentTours', icon: Repeat2,         path: '/permanent-tours', color: ICON_COLOR.violet, roles: STAFF },
      { titleKey: 'nav.recurringTrips', icon: CalendarClock,   path: '/recurring-trips', color: ICON_COLOR.purple, roles: STAFF },
    ],
  },
  // Resources — greens, for the things being scheduled and allocated.
  {
    labelKey: 'nav.groups.resources',
    items: [
      { titleKey: 'nav.planning',     icon: CalendarCheck, path: '/scheduler',     color: ICON_COLOR.teal },
      { titleKey: 'nav.tourPlanning', icon: Map,           path: '/tour-planning', color: ICON_COLOR.emerald, roles: STAFF },
      { titleKey: 'nav.fleet',        icon: Truck,         path: '/fleet-db',      color: ICON_COLOR.green,   roles: STAFF },
      { titleKey: 'nav.drivers',      icon: User,          path: '/drivers',       color: ICON_COLOR.lime,    roles: ADMIN },
      { titleKey: 'nav.institutions', icon: Building2,     path: '/institutions',  color: ICON_COLOR.teal,    roles: ADMIN },
    ],
  },
  // Administration — warm tones and violet, with the three system-level
  // entries (audit, settings, rules) muted so they recede.
  {
    labelKey: 'nav.groups.administration',
    items: [
      { titleKey: 'nav.users',     icon: Users,      path: '/users',     color: ICON_COLOR.violet,  roles: ADMIN },
      { titleKey: 'nav.reports',   icon: BarChart3,  path: '/reports',   color: ICON_COLOR.amber,   roles: STAFF },
      { titleKey: 'nav.resources', icon: FolderDown, path: '/resources', color: ICON_COLOR.orange,  roles: STAFF },
      { titleKey: 'nav.documents', icon: FileStack,  path: '/documents', color: ICON_COLOR.purple,  roles: STAFF },
      { titleKey: 'nav.auditLog',  icon: ScrollText, path: '/audit',     color: ICON_COLOR.muted,   roles: ADMIN },
      { titleKey: 'nav.devices',   icon: Tablet,     path: '/devices',   color: ICON_COLOR.rose,    roles: ADMIN },
      { titleKey: 'nav.settings',  icon: Settings,   path: '/settings',  color: ICON_COLOR.muted,   roles: ADMIN },
      { titleKey: 'nav.rules',     icon: Shield,     path: '/rules',     color: ICON_COLOR.muted,   roles: ADMIN },
      { titleKey: 'nav.widgets',   icon: LayoutGrid, path: '/widgets',   color: ICON_COLOR.pink,    roles: ADMIN },
      { titleKey: 'nav.plugins',   icon: Puzzle,     path: '/plugins',   color: ICON_COLOR.fuchsia, roles: ADMIN },
    ],
  },
];

/**
 * Circular collapse toggle pinned to the sidebar's right edge. Lives inside the
 * sidebar so it can read/toggle the provider state via `useSidebar`. Hidden in
 * the mobile (offcanvas) variant where a separate trigger applies.
 */
function SidebarCollapseToggle() {
  const { state, toggleSidebar, isMobile } = useSidebar();
  const { t } = useTranslation();
  if (isMobile) return null;
  const collapsed = state === 'collapsed';
  return (
    <button
      type="button"
      onClick={toggleSidebar}
      title={collapsed ? t('nav.expand') : t('nav.collapse')}
      aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
      className="absolute -right-[11px] top-[72px] z-30 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-[0_1px_6px_rgba(37,48,110,0.12)] transition-colors hover:text-primary"
    >
      {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
    </button>
  );
}

export default function MainLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { reset: resetPrefs } = usePreferencesStore();
  // SlotComponent is set by individual pages to inject controls into the header bar
  const { SlotComponent } = useHeaderSlotStore();
  const navigate = useNavigate();
  const location = useLocation();

  // The Scheduler page renders its own toolbar and uses the full viewport, so on
  // that route we suppress the shared top header AND drop the main padding for an
  // edge-to-edge toolbar + timeline. (Sidebar collapse/expand stays available via
  // the circular edge toggle.)
  const isSchedulerRoute = location.pathname === '/scheduler';

  /**
   * Initiates sidebar drag-to-resize.
   * Attaches mousemove/mouseup listeners to the document so the drag works
   * even if the cursor leaves the handle element. Clamps width between
   * MIN_SIDEBAR_WIDTH and 50% of the viewport.
   */
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    // Coalesce per-frame width writes — many mousemove events can fire
    // between repaints and each `--sidebar-width` change triggers layout
    // on the sidebar subtree. One write per frame is plenty.
    let liveWidth = MIN_SIDEBAR_WIDTH;
    let rafHandle: number | null = null;
    const flush = () => {
      rafHandle = null;
      document.getElementById('sidebar-provider')?.style.setProperty('--sidebar-width', `${liveWidth}px`);
    };

    const onMouseMove = (ev: MouseEvent) => {
      const maxWidth = window.innerWidth * 0.5;
      liveWidth = Math.min(Math.max(ev.clientX, MIN_SIDEBAR_WIDTH), maxWidth);
      if (rafHandle === null) rafHandle = requestAnimationFrame(flush);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (rafHandle !== null) {
        cancelAnimationFrame(rafHandle);
        // Make sure the final position is applied even if the rAF was queued
        // but the user released between writes.
        flush();
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  /** Clears auth state and preferences, then redirects to the login page. */
  const handleLogout = () => {
    logout();
    resetPrefs();
    navigate('/login');
  };

  // Filter nav items by the current user's roles, then drop any group left
  // empty. An entry's `roles` list is an OR — if any of the user's roles is in
  // the list, the entry is shown.
  const userRoles = user?.roles ?? [];
  const visibleGroups = NAV_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !item.roles || item.roles.some((r) => userRoles.includes(r as typeof userRoles[number])),
      ),
    }))
    .filter((group) => group.items.length > 0);

  /** Two-letter avatar initials derived from the user's display name. */
  const initials = (user?.name ?? '')
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    // h-svh ensures the layout fills exactly one viewport height (no page-level scroll)
    <SidebarProvider id="sidebar-provider" className="h-svh" style={{ '--sidebar-width': `${DEFAULT_SIDEBAR_WIDTH}px` } as React.CSSProperties}>
      <Sidebar collapsible="icon" className="relative">

        {/* Logo area — fills the header row, collapses in icon-only mode */}
        <div className="h-[60px] px-3 border-b flex items-center justify-center shrink-0">
          <img
            src={kabbiLogo}
            alt="Kabbi"
            className="max-h-[52px] w-auto group-data-[collapsible=icon]:max-h-[26px] transition-all"
          />
        </div>

        {/* Circular edge toggle to collapse / expand the rail */}
        <SidebarCollapseToggle />

        {/* Grouped navigation */}
        <SidebarContent className="gap-0 px-2 pt-1.5">
          {visibleGroups.map((group, index) => (
            <React.Fragment key={group.labelKey}>
              {/* Rule BETWEEN groups only — never above the first or below the
                  last, where it would read as a border on the sidebar itself.
                  Groups are role-filtered, so "first" is whichever survives the
                  filter for this user, not a fixed one. */}
              {/* ⚠️ `mx-0` is required, not cosmetic. `SidebarSeparator` ships
                  `mx-2 w-auto`, but the underlying `Separator` sets
                  `data-[orientation=horizontal]:w-full`, whose attribute
                  selector out-specifies the plain `w-auto` — so the rule is
                  100% wide AND carries 8px side margins, overflowing its
                  container by 16px and giving `SidebarContent`
                  (`overflow-auto`) a permanent horizontal scrollbar. The
                  container's own `px-2` provides the inset instead. */}
              {index > 0 && <SidebarSeparator className="mx-0 my-1.5" />}
              <SidebarGroup className="px-0 py-0">
                <SidebarGroupLabel className="px-2.5 pt-3 pb-1 h-auto text-[10px] font-bold uppercase tracking-[0.1em] text-foreground">
                  {t(group.labelKey)}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="gap-px">
                    {group.items.map((item) => {
                      const active = location.pathname === item.path;
                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton
                            asChild
                            isActive={active}
                            tooltip={t(item.titleKey)}
                            className={`h-auto gap-[9px] rounded-[9px] px-2.5 py-[7px] text-[13px] font-normal leading-snug text-sidebar-foreground hover:bg-sidebar-accent hover:text-primary data-[active=true]:bg-primary/10 data-[active=true]:font-semibold data-[active=true]:text-primary [&>svg]:size-4`}
                          >
                            <Link to={item.path}>
                              <item.icon className={item.color} strokeWidth={active ? 2.3 : 1.8} />
                              <span>{t(item.titleKey)}</span>
                              {active && (
                                <span className="ml-auto h-1.5 w-1.5 rounded-full bg-brand group-data-[collapsible=icon]:hidden" />
                              )}
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </React.Fragment>
          ))}
        </SidebarContent>

        {/* User account dropdown pinned to the sidebar footer */}
        <SidebarFooter className="border-t">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-2.5 px-2 h-auto py-2">
                <Avatar className="h-[30px] w-[30px] shrink-0">
                  <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-[11px] font-bold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                {/* Name and role are hidden in icon-only (collapsed) mode */}
                <div className="text-left group-data-[collapsible=icon]:hidden min-w-0">
                  <div className="text-[12.5px] font-semibold leading-none truncate text-foreground">{user?.name}</div>
                  <div className="text-[10.5px] text-muted-foreground mt-0.5">{user?.roles.join(', ')}</div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-56">
              <DropdownMenuLabel>{t('common.myAccount')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile">
                  <User className="mr-2 h-4 w-4" />
                  {t('nav.profile')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings">
                  <Settings className="mr-2 h-4 w-4" />
                  {t('nav.settings')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                {t('common.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>

        {/* Drag handle for sidebar resize — hidden in collapsed icon-only mode */}
        <div
          className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/20 active:bg-primary/40 transition-colors z-20 group-data-[collapsible=icon]:hidden"
          onMouseDown={startResize}
        />
      </Sidebar>

      {/* Main content area (right of the sidebar) */}
      <SidebarInset>
        {/* Top header bar — an optional per-page controls slot (search, filters,
            actions) injected via useHeaderSlotStore. `bg-card` (pure white) so the
            header reads crisp white like the reference, not the cool-grey app
            background showing through. Suppressed on the Scheduler route so the
            timeline reclaims the full height. */}
        {!isSchedulerRoute && (
          <header className="flex items-center gap-2 border-b px-4 h-[60px] shrink-0 overflow-x-auto bg-card">
            {SlotComponent ? <SlotComponent /> : <div className="flex-1" />}
          </header>
        )}
        {/* overflow-hidden delegates scrolling to individual page components */}
        {/* Suspense covers all lazy-loaded page chunks defined in App.tsx */}
        {/* Uniform p-4 on every route. Full-bleed pages (Dispatcher, Scheduler)
            wrap their content in `-m-4` to escape it symmetrically; an
            asymmetric value here (e.g. pr-0) would leave that `-m-4` over-pulling
            on one side and overflowing `overflow-hidden` — clipping the edge. */}
        <main className="flex-1 min-h-0 overflow-hidden p-4">
          <Suspense fallback={
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t('common.loading')}
            </div>
          }>
            <Outlet />
          </Suspense>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
